import { Box, CircularProgress, Typography } from "@mui/material";
import { MainPadding } from "../../styles/Main.styles";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import TableHeader from "../../components/table/TableHeader";
import { useEffect, useRef, useState } from "react";
import { useGetInvoice } from "../invoice/network/resolver";
import ReportGraphicFilter from "./__components/ReportGraphicFilter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function ReportGraphic() {
  const refChart = useRef<any>();
  const [startDate, setStartDate] = useState<undefined | string>(undefined);
  const [endDate, setEndDate] = useState("");
  const [selectedType, setSelectedType] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>();
  const [selectedYear, setSelectedYear] = useState<string | undefined>();
  const { data: dataRawInvoice, isLoading: loadingInvoice } = useGetInvoice({
    enabledQuery: startDate !== undefined,
    start_date: startDate,
    end_date: endDate,
    status: 3,
    perPage: 9999999,
  });
  const dataInvoice = dataRawInvoice?.data.data.data;
  const [dataChart, setDataChart] = useState({
    labels: [],
    datasets: [],
  });

  function groupByDayFull(data: any) {
    if (data.length === 0) return [];

    // Detect year + month from first data item
    const firstDate = new Date(data[0].created_at);
    const year = firstDate.getFullYear();
    const month = firstDate.getMonth(); // 0-index

    // Find number of days in that month
    const lastDay = new Date(year, month + 1, 0).getDate();

    // Step 1: Group real data
    const grouped = data.reduce((acc: any, item: any) => {
      const d = new Date(item.created_at);
      const day = d.getDate(); // 1 - 31
      acc[day] = (acc[day] || 0) + item.total_profit;
      return acc;
    }, {});

    // Step 2: Build full list of days
    const result = [];
    for (let day = 1; day <= lastDay; day++) {
      const dateString = `${year}-${String(month + 1).padStart(
        2,
        "0"
      )}-${String(day).padStart(2, "0")}`;

      result.push({
        date: dateString,
        total_profit: grouped[day] || 0,
      });
    }

    return result;
  }

  function groupByMonthFull(data: any) {
    if (data.length === 0) return [];

    // Detect the year from the first data item
    const detectedYear = new Date(data[0].created_at).getFullYear();

    // Step 1: Group existing data
    const grouped = data.reduce((acc: any, item: any) => {
      const date = new Date(item.created_at);
      const year = date.getFullYear();

      // Only count months for the detected year
      if (year !== detectedYear) return acc;

      const monthKey = `${year}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      acc[monthKey] = (acc[monthKey] || 0) + item.total_profit;
      return acc;
    }, {});

    // Step 2: Fill all months
    const result = [];
    for (let month = 1; month <= 12; month++) {
      const monthKey = `${detectedYear}-${String(month).padStart(2, "0")}`;
      result.push({
        month: monthKey,
        total_profit: grouped[monthKey] || 0,
      });
    }

    return result;
  }

  const getLastDayOfMonth = (year: any, month: any) => {
    return new Date(year, month, 0).getDate();
  };

  const generateDateArray = (year: any, month: any) => {
    const lastDay = new Date(year, month, 0).getDate();
    return Array.from({ length: lastDay }, (_, i) => i + 1);
  };

  useEffect(() => {
    if (dataInvoice && !loadingInvoice) {
      if (selectedType === 1 && selectedMonth) {
        const tempMonthNumber = selectedMonth.split("-")[0];
        const daily = groupByDayFull(dataInvoice).map(
          (item) => item.total_profit
        );
        const temp = {
          labels: generateDateArray(selectedYear, tempMonthNumber) as any,
          datasets: [
            {
              label: "Profit",
              data: daily,
              borderWidth: 2,
              borderColor: "#016B61",
              backgroundColor: "#016B61",
            },
          ],
        };
        setDataChart(temp as any);
      } else {
        const monthly = groupByMonthFull(dataInvoice).map(
          (item) => item.total_profit
        );
        const temp = {
          labels: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "Mei",
            "Jun",
            "Jul",
            "Agu",
            "Sep",
            "Okt",
            "Nov",
            "Des",
          ] as any,
          datasets: [
            {
              label: "Profit",
              data: monthly,
              borderWidth: 2,
              borderColor: "#016B61",
              backgroundColor: "#016B61",
            },
          ],
        };
        setDataChart(temp as any);
      }
    }
  }, [dataInvoice, selectedType]);

  useEffect(() => {
    if (selectedYear) {
      if (selectedType === 1) {
        if (selectedMonth) {
          // Bulan
          const startDate = `${selectedYear}-${selectedMonth}`;
          const tempMonthNumber = selectedMonth.split("-")[0];
          const lastDayOfMonth = getLastDayOfMonth(
            selectedYear,
            tempMonthNumber
          );
          const endDate = `${selectedYear}-${tempMonthNumber}-${lastDayOfMonth}`;
          setStartDate(startDate);
          setEndDate(endDate);
        }
      } else {
        // Tahun
        const startDate = `${selectedYear}-01-31`;
        const endDate = `${selectedYear}-12-31`;
        setStartDate(startDate);
        setEndDate(endDate);
      }
    }
  }, [selectedMonth, selectedYear, selectedType]);

  return (
    <MainPadding>
      <TableHeader
        title="Filter Tanggal Report"
        renderFilter={
          <ReportGraphicFilter
            onMonthChange={(e) => setSelectedMonth(e)}
            onTypeChange={(e) => setSelectedType(e)}
            onYearChange={(e) => setSelectedYear(e)}
          />
        }
      />
      <Box bgcolor={"white"} p={1}>
        {startDate === undefined ? (
          <Box display={"flex"} justifyContent={"center"} mt={1}>
            <Typography>Pilih Tanggal Untuk Melihat Report</Typography>
          </Box>
        ) : (
          <Box>
            {loadingInvoice ? (
              <Box display={"flex"} justifyContent={"center"} mt={1}>
                <CircularProgress size={20} />
              </Box>
            ) : !dataInvoice || dataInvoice.length <= 0 ? (
              <Box display={"flex"} justifyContent={"center"} mt={1}>
                <Typography>
                  Tidak Ada Data Untuk Tanggal Yang Dipilih
                </Typography>
              </Box>
            ) : (
              <Line
                ref={refChart}
                data={dataChart}
                options={{
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      min: 0,
                    },
                  },
                }}
              />
            )}
          </Box>
        )}
      </Box>
    </MainPadding>
  );
}
